// src/pages/Examples.tsx
import { useState, useEffect } from "react";
import { motion, LayoutGroup, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ShowCard from "../components/ShowCard";
import { ARCHETYPES } from "../constants/archetypes";
import { ExampleExperience, getRandomExamplesByArchetype } from "../constants/example-experiences";
import { tiktokPixelService } from "../services/tiktok-pixel.service";

export default function Examples() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [availableHeight, setAvailableHeight] = useState(window.innerHeight);
  const [displayExamples, setDisplayExamples] = useState<ExampleExperience[]>([]);
  const dragX = useMotionValue(0);

  useEffect(() => {
    setDisplayExamples(getRandomExamplesByArchetype());
  }, []);

  const activeExample = displayExamples[activeIndex];
  const activeArchetype = activeExample?.archetype;

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { damping: 50, stiffness: 100 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  const bgX1 = useTransform(smoothX, [-0.5, 0.5], ["-15%", "15%"]);
  const bgY1 = useTransform(smoothY, [-0.5, 0.5], ["-15%", "15%"]);
  const bgX2 = useTransform(smoothX, [-0.5, 0.5], ["10%", "-10%"]);
  const bgY2 = useTransform(smoothY, [-0.5, 0.5], ["10%", "-10%"]);

  const handleMouseMove = (e: React.MouseEvent) => {
    mouseX.set(e.clientX / window.innerWidth - 0.5);
    mouseY.set(e.clientY / window.innerHeight - 0.5);
  };

  const themeColors = {
    moments: {
      primary: "rgba(234, 179, 8, 0.6)",
      secondary: "rgba(234, 179, 8, 0.25)",
    },
    heartbeat: {
      primary: "rgba(239, 68, 68, 0.6)",
      secondary: "rgba(239, 68, 68, 0.25)",
    },
    safehaven: {
      primary: "rgba(59, 130, 246, 0.6)",
      secondary: "rgba(59, 130, 246, 0.25)",
    },
    default: {
      primary: "rgba(255, 255, 255, 0.15)",
      secondary: "rgba(255, 255, 255, 0.05)",
    },
  } as const;

  const theme = activeArchetype ? themeColors[activeArchetype] : themeColors.default;
  const archetypeName = activeArchetype ? ARCHETYPES[activeArchetype]?.name : null;

  useEffect(() => {
    const handleResize = () => setAvailableHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (displayExamples.length === 0) return;

    tiktokPixelService.trackViewContent(
      {
        content_id: "example_experience_gallery",
        content_type: "product_group",
        content_name: "LRNSY Emotional Examples",
      },
      0,
      "KES",
      "viewcontent:home:examples"
    );
  }, [displayExamples]);

  const handleDragEnd = () => {
    if (displayExamples.length === 0) return;

    const x = dragX.get();
    const threshold = window.innerWidth <= 768 ? 5 : 50;

    if (x <= -threshold) {
      setActiveIndex((prev) => (prev + 1) % displayExamples.length);
    } else if (x >= threshold) {
      setActiveIndex((prev) => (prev - 1 + displayExamples.length) % displayExamples.length);
    }

    dragX.set(0);
  };

  const handleExampleClick = (index: number) => {
    const example = displayExamples[index];
    if (!example) return;

    if (index !== activeIndex) {
      setActiveIndex(index);
      return;
    }

    tiktokPixelService.track(
      "SelectExample",
      {
        contents: [
          {
            content_id: example.id,
            content_type: "product",
            content_name: `${example.title} (${example.archetype})`,
          },
        ],
      },
      `selectexample:${example.id}`
    );

    navigate(`/create?archetype=${example.archetype}&example=${example.id}`);
  };

  if (displayExamples.length === 0) {
    return null;
  }

  return (
    <div
      onMouseMove={handleMouseMove}
      className="fixed inset-0 bg-black text-text-primary overflow-hidden font-sans"
    >
      <div className="absolute inset-0 pointer-events-none z-0">
        <motion.div
          animate={{ backgroundColor: theme.secondary }}
          transition={{ duration: 1 }}
          className="absolute inset-0 opacity-50 blur-3xl"
        />

        <motion.div
          style={{ x: bgX1, y: bgY1, transition: "background-color 0.8s ease" }}
          animate={{ backgroundColor: theme.primary }}
          className="absolute top-[-10%] left-[-10%] w-[120vw] h-[120vw] rounded-full blur-[140px] md:blur-[220px] opacity-40 mix-blend-screen"
        />

        <motion.div
          style={{ x: bgX2, y: bgY2, transition: "background-color 1s ease" }}
          animate={{ backgroundColor: theme.primary }}
          className="absolute bottom-[-20%] right-[-10%] w-[100vw] h-[100vw] rounded-full blur-[120px] md:blur-[200px] opacity-30 mix-blend-screen"
        />

        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_20%,rgba(0,0,0,0.7)_100%)]" />
      </div>

      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="absolute top-16 md:top-20 left-0 right-0 z-40 flex justify-center px-4"
      >
        <div
          className="backdrop-blur-2xl border px-5 md:px-6 py-3 rounded-full transition-all duration-700 shadow-lg text-center"
          style={{
            backgroundColor: theme.secondary,
            borderColor: theme.primary,
            boxShadow: `0 0 30px ${theme.secondary}`,
          }}
        >
          <span className="block text-xs md:text-sm font-semibold text-white ">
            {archetypeName}
          </span>
        </div>
      </motion.div>

      <LayoutGroup>
        <motion.main
          key="gallery"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative flex mt-24 md:mt-28 flex-col items-center w-full h-[86vh] pt-[8vh] px-4 z-10"
        >
          <div
            className="relative w-full flex items-center justify-center"
            style={{ height: availableHeight * 0.65 }}
          >
            <motion.div
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              style={{ x: dragX }}
              onDragEnd={handleDragEnd}
              className="relative w-full flex items-center justify-center perspective-1000 cursor-grab active:cursor-grabbing z-20"
            >
              {displayExamples.map((example, index) => {
                let offset = index - activeIndex;
                const count = displayExamples.length;

                if (offset > count / 2) offset -= count;
                if (offset < -count / 2) offset += count;

                return (
                  <ShowCard
                    key={example.id}
                    example={example}
                    offset={offset}
                    isActive={index === activeIndex}
                    onClick={() => handleExampleClick(index)}
                  />
                );
              })}
            </motion.div>
          </div>

          <div className="absolute bottom-2 md:bottom-1 left-0 right-0 z-30 px-6 text-center pointer-events-none">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.35em] text-white/35 font-bold mb-3">
              Tap the centered example to create yours
            </p>

            <h1 className="text-white text-xl md:text-xl lg:text-2xl font-black tracking-tight uppercase">
              {activeExample.title}
            </h1>
          </div>
        </motion.main>
      </LayoutGroup>
    </div>
  );
}
